import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, MessageCircle, Copy, Pencil, Star } from "lucide-react";
import { Modal, Field } from "@/pages/Agenda";
import { whatsappLink } from "@/lib/format";

const CATEGORIES = [
  { id: "confirmacao", label: "Confirmação" },
  { id: "lembrete", label: "Lembrete" },
  { id: "reagendamento", label: "Reagendamento" },
  { id: "cancelamento", label: "Cancelamento" },
  { id: "atraso", label: "Atraso" },
  { id: "cobranca", label: "Cobrança" },
  { id: "manutencao", label: "Manutenção" },
  { id: "reativacao", label: "Reativação" },
  { id: "aniversario", label: "Aniversário" },
  { id: "agradecimento", label: "Agradecimento" },
  { id: "avaliacao", label: "Pedido de avaliação" },
  { id: "ajuste_preco", label: "Ajuste de preço" },
  { id: "outro", label: "Outro" },
];

const VARIABLES = ["{nome}", "{servico}", "{data}", "{hora}", "{valor}", "{studio}", "{profissional}"];

export default function Mensagens() {
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("");

  const load = async () => setItems((await api.get("/message-templates")).data);
  useEffect(() => { load(); }, []);

  const copy = async (t) => {
    try { await navigator.clipboard.writeText(t.content); toast.success("Copiado!"); }
    catch { toast.error("Não foi possível copiar."); }
  };

  const filtered = filter ? items.filter(t => t.category === filter) : items;

  return (
    <div className="space-y-4" data-testid="messages-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-[#27272A]">Mensagens</h1>
          <p className="text-[#71717A] text-sm mt-1">Modelos prontos para enviar pelo WhatsApp.</p>
        </div>
        <button data-testid="btn-new-template" onClick={() => { setEditing(null); setShowModal(true); }} className="btn-primary-nc flex items-center gap-1 text-sm">
          <Plus size={16} /> Novo modelo
        </button>
      </div>

      <div className="flex gap-2 overflow-x-auto hide-scrollbar" data-testid="msg-categories">
        <button onClick={() => setFilter("")}
          className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold ${!filter ? "bg-[#682F3E] text-white" : "bg-white text-[#71717A] border border-[#E4E4E7]"}`}>
          Todos
        </button>
        {CATEGORIES.map(c => (
          <button key={c.id} onClick={() => setFilter(c.id)} data-testid={`filter-${c.id}`}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold ${filter === c.id ? "bg-[#682F3E] text-white" : "bg-white text-[#71717A] border border-[#E4E4E7]"}`}>
            {c.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="card-nc p-8 text-center" data-testid="messages-empty">
          <p className="text-[#71717A] mb-3">Nenhum modelo nesta categoria.</p>
        </div>
      ) : (
        <div className="grid gap-2">
          {filtered.map(t => (
            <div key={t.id} data-testid={`tpl-${t.id}`} className="card-nc p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="badge-status bg-[#F5ECE8] text-[#682F3E]">
                    {CATEGORIES.find(c => c.id === t.category)?.label || t.category}
                  </span>
                  <div className="font-semibold text-[#27272A]">{t.name}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => copy(t)} data-testid={`copy-${t.id}`} className="text-[#71717A] p-2 hover:text-[#682F3E]"><Copy size={16} /></button>
                  <a href={whatsappLink("", t.content)} target="_blank" rel="noreferrer" data-testid={`wa-${t.id}`} className="text-[#4F7A61] p-2"><MessageCircle size={16} /></a>
                  <button onClick={() => { setEditing(t); setShowModal(true); }} data-testid={`edit-${t.id}`} className="text-[#71717A] p-2"><Pencil size={16} /></button>
                </div>
              </div>
              <p className="text-sm text-[#27272A] whitespace-pre-wrap">{t.content}</p>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <TemplateModal initial={editing} onClose={() => setShowModal(false)}
          onSaved={async () => { setShowModal(false); await load(); toast.success("Modelo salvo!"); }} />
      )}
    </div>
  );
}

function TemplateModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: initial?.name || "", category: initial?.category || "outro",
    content: initial?.content || "", favorite: initial?.favorite || false,
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const insertVar = (v) => set("content", (form.content + " " + v).trim());

  const save = async () => {
    if (!form.name.trim() || !form.content.trim()) { toast.error("Preencha nome e conteúdo."); return; }
    setLoading(true);
    try {
      if (initial) await api.patch(`/message-templates/${initial.id}`, form);
      else await api.post("/message-templates", form);
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  const remove = async () => {
    if (!window.confirm("Excluir modelo?")) return;
    await api.delete(`/message-templates/${initial.id}`);
    onSaved();
  };

  return (
    <Modal onClose={onClose} title={initial ? "Editar modelo" : "Novo modelo"} testid="modal-template">
      <Field label="Nome"><input data-testid="tpl-name" value={form.name} onChange={e => set("name", e.target.value)} className="input-nc" /></Field>
      <Field label="Categoria">
        <select data-testid="tpl-cat" value={form.category} onChange={e => set("category", e.target.value)} className="input-nc">
          {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </Field>
      <Field label="Mensagem">
        <textarea data-testid="tpl-content" rows={5} value={form.content} onChange={e => set("content", e.target.value)} className="input-nc" />
      </Field>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-[#71717A] mb-1">Variáveis (clique para inserir)</div>
        <div className="flex flex-wrap gap-1.5">
          {VARIABLES.map(v => (
            <button key={v} onClick={() => insertVar(v)} type="button" data-testid={`var-${v}`}
              className="px-2.5 py-1 rounded-full bg-[#F5ECE8] text-[#682F3E] text-xs font-mono-brand font-semibold">
              {v}
            </button>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        {initial && <button onClick={remove} data-testid="tpl-delete" className="text-[#B76E56] p-3 hover:bg-[#F9ECE8] rounded-xl"><Trash2 size={18} /></button>}
        <button data-testid="tpl-save" onClick={save} disabled={loading} className="btn-primary-nc flex-1">
          {loading ? "Salvando…" : "Salvar"}
        </button>
      </div>
    </Modal>
  );
}
